import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { getImageUrl } from "@app/helpers/getUploadUrl";
import SortableList, { SortableItem } from "react-easy-sort";
import { useNavigate } from "react-router-dom";

import { useTeamOrderingLogic } from "./useTeamOrderingLogic";
import {
  Actions,
  EmptyState,
  MemberImage,
  OrderNumber,
  PageContainer,
  SortCard,
  StatusBadge,
  Toolbar,
} from "./styled";

const TeamOrdering = () => {
  const navigate = useNavigate();
  const {
    teamList,
    hasChanges,
    isLoading,
    isSaving,
    moveTeam,
    onSortEnd,
    resetOrdering,
    saveOrdering,
  } = useTeamOrderingLogic()

  return (
    <PageContainer>
      <Button
        color="inherit"
        startIcon={<ArrowBackRoundedIcon />}
        sx={{ alignSelf: "flex-start" }}
        onClick={() => navigate(-1)}
      >
        Back to team
      </Button>

      <Toolbar>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Team ordering
          </Typography>
          <Typography color="text.secondary" mt={0.75}>
            Arrange how team members appear on the website.
          </Typography>
        </Box>

        <Actions>
          <StatusBadge $hasChanges={hasChanges}>
            <CheckCircleRoundedIcon fontSize="small" />
            {hasChanges ? "Unsaved changes" : "Order is saved"}
          </StatusBadge>
          <Button
            color="inherit"
            startIcon={<RestartAltRoundedIcon />}
            disabled={!hasChanges || isSaving}
            onClick={resetOrdering}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress color="inherit" size={18} /> : <SaveRoundedIcon />}
            disabled={!hasChanges || isSaving}
            onClick={saveOrdering}
          >
            {isSaving ? "Saving..." : "Save order"}
          </Button>
        </Actions>
      </Toolbar>

      <Alert severity="info">
        Drag members using the handle, or use the arrow buttons. Changes are only published after you save.
      </Alert>

      {isLoading ? (
        <EmptyState>
          <CircularProgress size={30} />
          <Typography color="text.secondary">Loading team members...</Typography>
        </EmptyState>
      ) : teamList.length === 0 ? (
        <EmptyState>
          <GroupsRoundedIcon color="disabled" sx={{ fontSize: 48 }} />
          <Typography variant="h6">No team members yet</Typography>
          <Typography color="text.secondary">
            Add team members before setting their order.
          </Typography>
        </EmptyState>
      ) : (
        <SortableList
          onSortEnd={onSortEnd}
          lockAxis="y"
          draggedItemClassName="dragged"
          className="sort-list"
        >
          {teamList.map((item, index) => {
            const image = item.images.find(({ isMain }) => isMain) || item.images[0]

            return (
              <SortableItem key={item.id}>
                <SortCard>
                  <DragIndicatorRoundedIcon className="drag-handle" />
                  <OrderNumber>{index + 1}</OrderNumber>
                  {image ? (
                    <MemberImage src={getImageUrl(image.url)} alt={item.nameEn} />
                  ) : (
                    <MemberImage as="div">
                      <GroupsRoundedIcon color="disabled" />
                    </MemberImage>
                  )}
                  <Box minWidth={0} flex={1}>
                    <Typography fontWeight={700} noWrap>
                      {item.nameEn}
                    </Typography>
                    <Typography color="text.secondary" variant="body2" noWrap>
                      {item.positionEn || "No position added"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton
                          aria-label={`Move ${item.nameEn} up`}
                          disabled={index === 0}
                          onClick={() => moveTeam(index, index - 1)}
                        >
                          <KeyboardArrowUpRoundedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          aria-label={`Move ${item.nameEn} down`}
                          disabled={index === teamList.length - 1}
                          onClick={() => moveTeam(index, index + 1)}
                        >
                          <KeyboardArrowDownRoundedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </SortCard>
              </SortableItem>
            )
          })}
        </SortableList>
      )}
    </PageContainer>
  )
};

export default TeamOrdering;
